import React from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from 'src/contexts/ThemeContext';
import { cn } from 'src/lib/utils';

// Plain square icon button — matches the shared HTML template's own
// `.icon-btn.theme-toggle` treatment exactly: 32×32, 1px border, transparent
// fill at rest, brand-tinted border/background/icon on hover. Replaces the
// previous slide-knob pill switch.
//
// Token mapping from the template's CSS vars to this app's own (all already
// used elsewhere — nothing invented): var(--text-sec) → text-muted-foreground,
// var(--border) → border-border, var(--brand) → text-primary/border-primary,
// var(--brand-subtle) → bg-brand-bg-subtle, var(--radius) (8px) → rounded-lg
// (this app's own --radius is also 8px, and it's the Button component's own
// base radius).
//
// The template's own script only ever swaps aria-checked/aria-label/title —
// it defines just one `#i-sun` symbol in its <defs>, no moon, so the glyph
// itself never actually changes there. Reads as a mockup limitation rather
// than an intentional "always show sun": a fixed icon would give no visual
// signal of the current theme at all, so swapping Sun/Moon by `isDark` is
// the one addition on top of the template's own markup. aria-label/title
// split does match the template exactly, though — aria-label states the
// current mode, title states the action a click takes.
export function ThemeToggle({ className }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const Icon = isDark ? Moon : Sun;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      onClick={toggleTheme}
      aria-label={isDark ? 'Dark mode' : 'Light mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={cn(
        'inline-flex items-center justify-center w-8 h-8 rounded-lg border border-border bg-transparent text-muted-foreground cursor-pointer transition-colors hover:text-primary hover:border-primary hover:bg-brand-bg-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        className
      )}
    >
      <Icon className="h-[15px] w-[15px]" strokeWidth={1.9} />
    </button>
  );
}

export default ThemeToggle;
